import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useContext, useEffect } from 'react'
import { getLocations, getLocationsByCNTID, getContacts } from '../api/ncs'
import { JobAppContext } from '../GlobalState'

/**
 * Hook to fetch locations from NCS API
 * @param {string} apiKey - API key for authentication
 * @returns {object} Query result with locations data, loading state, etc.
 */
export const useLocations = (apiKey) => {
	return useQuery({
		queryKey: ['locations', apiKey],
		queryFn: () => getLocations(apiKey),
		enabled: !!apiKey,
		staleTime: 10 * 60 * 1000, // 10 minutes - locations don't change often
	})
}

/**
 * Hook to fetch locations by contact ID
 * @param {number} cnt_id - Contact ID
 * @param {string} apiKey - API key for authentication
 * @returns {object} Query result with locations data, loading state, etc.
 */
export const useLocationsByCNTID = (cnt_id, apiKey) => {
	return useQuery({
		queryKey: ['locations', 'contact', cnt_id, apiKey],
		queryFn: () => getLocationsByCNTID(cnt_id, apiKey),
		enabled: !!cnt_id && cnt_id !== 0 && !!apiKey,
		staleTime: 10 * 60 * 1000, // 10 minutes
	})
}

/**
 * Hook to fetch contacts by division ID
 * @param {string} div_id - Division ID
 * @param {string} apiKey - API key for authentication
 * @returns {object} Query result with contacts data, loading state, etc.
 */
export const useContacts = (div_id, apiKey) => {
	// Use a placeholder key when div_id is null to avoid creating unnecessary cache entries
	const queryKey = div_id ? ['contacts', div_id, apiKey] : ['contacts-placeholder'];
	
	return useQuery({
		queryKey: queryKey,
		queryFn: () => getContacts(div_id, apiKey),
		enabled: !!div_id && !!apiKey,
		staleTime: 10 * 60 * 1000, // 10 minutes
	})
}

/**
 * Hook to update locations list (for manual refetch)
 * This returns a function that can be used to refetch locations
 */
export const useUpdateLocations = () => {
	const queryClient = useQueryClient()
	
	return {
		updateLocations: (cnt_id, apiKey) => {
			if (cnt_id && cnt_id !== 0) {
				queryClient.invalidateQueries({ queryKey: ['locations', 'contact', cnt_id] })
			} else {
				queryClient.invalidateQueries({ queryKey: ['locations', apiKey] })
			}
		}
	}
}

/**
 * Hook to update contacts list (for manual refetch)
 * This returns a function that can be used to refetch contacts
 */
export const useUpdateContacts = () => {
	const queryClient = useQueryClient()
	
	return {
		updateContacts: (div_id) => {
			queryClient.invalidateQueries({ queryKey: ['contacts', div_id] })
		}
	}
}

/**
 * Component that prefetches NCS data (locations and contacts) when user is authenticated.
 * Place this inside QueryClientProvider to enable prefetching on login.
 * 
 * This ensures data is available immediately when users navigate to:
 * - Add Job modal
 * - Job Settings
 * - Users page
 */
export const NcsDataPrefetcher = () => {
	const queryClient = useQueryClient()
	const { apiKey, isAuthenticated } = useContext(JobAppContext)

	useEffect(() => {
		if (isAuthenticated && apiKey) {
			// Prefetch locations - these are needed for Add Job, Job Settings, etc.
			queryClient.prefetchQuery({
				queryKey: ['locations', apiKey],
				queryFn: () => getLocations(apiKey),
				staleTime: 10 * 60 * 1000, // 10 minutes
			}).then(() => {
				// Once locations are loaded, prefetch contacts for the first location
				const locations = queryClient.getQueryData(['locations', apiKey])
				if (locations?.length > 0) {
					const firstDivId = locations[0]?.DIV_ID
					if (firstDivId) {
						queryClient.prefetchQuery({
							queryKey: ['contacts', firstDivId, apiKey],
							queryFn: () => getContacts(firstDivId, apiKey),
							staleTime: 10 * 60 * 1000, // 10 minutes
						})
					}
				}
			})
		}
	}, [isAuthenticated, apiKey, queryClient])

	return null // This component doesn't render anything
}
